import { ContentState } from "draft-js";
import { I18n } from "@assets/strings";
import { List, Set } from "immutable";
import React, { useState } from "react";
import { useHistory } from "@core/routing/useHistory";
import { ClipLoader } from "react-spinners";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { useArticlesWorkspaces } from "@domain/group/workspace/hooks";
import { Workspace } from "@domain/group/workspace/workspace";
import { Article, ArticleDraft } from "@domain/lms/article/article";
import { useArticle } from "@domain/lms/article/hooks";
import { Page } from "@ui/components/page";
import { NotFoundPage } from "@ui/pages/notFoundPage";
import { ArticleEditor } from "./articleEditor";

interface ArticleEditorPageProps extends React.HTMLAttributes<HTMLDivElement> {
	articleId?: string;
	workspaceId?: string;
}

export const ArticleEditorPage: React.FC<ArticleEditorPageProps> = ({ articleId, workspaceId, ...htmlProps }) => {
	return articleId ? (
		<ArticleUpdatePage articleId={articleId} {...htmlProps} />
	) : (
		<ArticleCreationPage workspaceId={workspaceId} {...htmlProps} />
	);
};

interface ArticleUpdatePageProps extends ArticleEditorPageProps {
	articleId: string;
}
const ArticleUpdatePage: React.FC<ArticleUpdatePageProps> = ({ articleId, ...htmlProps }) => {
	const { result: initialArticle, loading, error } = useArticle(articleId);
	const { result: workspaces, loading: workspacesLoading } = useArticlesWorkspaces(articleId);
	const [articleEditionStep, setArticleEditionStep] = useState<"MAIN" | "DETAILS">("MAIN");
	const history = useHistory();

	if (error?.code === 404) {
		return <NotFoundPage message={I18n.t("404.article")} />;
	}
	const finalArticle = convertSectionsAndAssetsToList(initialArticle, workspaces);
	return (
		<Page
			{...htmlProps}
			backAction={articleEditionStep === "DETAILS" ? () => setArticleEditionStep("MAIN") : () => history.goBack()}
			backText={articleEditionStep === "DETAILS" ? I18n.t("article.editor.step.back") : undefined}
			header={articleEditionStep === "DETAILS" ? undefined : I18n.t("article.editor.header")}
		>
			{loading || workspacesLoading ? (
				<ClipLoader />
			) : (
				finalArticle && (
					<ArticleEditor
						step={articleEditionStep}
						onStepChange={setArticleEditionStep}
						initialArticle={finalArticle}
						articleId={articleId}
					/>
				)
			)}
		</Page>
	);
};

interface ArticleCreatePageProps extends React.HTMLAttributes<HTMLDivElement> {
	workspaceId?: string;
}
const ArticleCreationPage: React.FC<ArticleCreatePageProps> = ({ workspaceId, ...htmlProps }) => {
	const currentProfile = useCurrentProfile();
	const [articleEditionStep, setArticleEditionStep] = useState<"MAIN" | "DETAILS">("MAIN");
	const history = useHistory();

	const initialArticle: ArticleDraft = {
		contentType: "",
		title: "",
		description: ContentState.createFromText(""),
		language: currentProfile?.language ?? "ENGLISH",
		status: "DRAFT",
		visibility: "PUBLIC",
		keepOwnership: true,
		duration: 0,
		content: {
			sections: List(),
			assets: List(),
			tags: List(),
		},
		tags: [],
		workspaceIds: Set(workspaceId ? [workspaceId] : []),
		articleType: undefined,
	};

	return (
		<Page
			{...htmlProps}
			backAction={articleEditionStep === "DETAILS" ? () => setArticleEditionStep("MAIN") : () => history.goBack()}
			backText={articleEditionStep === "DETAILS" ? I18n.t("article.editor.step.back") : undefined}
			header={articleEditionStep === "DETAILS" ? undefined : I18n.t("article.editor.new.header")}
		>
			<ArticleEditor initialArticle={initialArticle} step={articleEditionStep} onStepChange={setArticleEditionStep} />
		</Page>
	);
};

function convertSectionsAndAssetsToList(initialArticle: Article | null, workspaces: Workspace[]): ArticleDraft | null {
	if (!initialArticle) {
		return null;
	}

	return {
		...initialArticle,
		workspaceIds: Set(workspaces.map(({ id }) => id)),
		content: {
			...initialArticle.content,
			sections: List(initialArticle.content.sections),
			assets: List(initialArticle.content.assets),
			tags: List(initialArticle.content.tags),
		},
	};
}
